import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import FollowButton from './FollowButton';
import BlockButton from './BlockButton';
import { FollowUserProfile } from '../../types/follow';

interface UserProfileCardProps {
  user: FollowUserProfile;
  showFollowButton?: boolean;
  showBlockButton?: boolean;
  showStats?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'outlined' | 'elevation';
  onClick?: (userId: string) => void;
  isLoading?: boolean;
  onBlockChange?: (isBlocked: boolean) => void;
}

/**
 * User profile card component for displaying user information
 */
const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  showFollowButton = true,
  showBlockButton = false,
  showStats = true,
  size = 'medium',
  variant = 'elevation',
  onClick,
  isLoading = false,
  onBlockChange,
}) => {
  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on follow button
    if ((e.target as HTMLElement).closest('.follow-button')) {
      return;
    }
    
    if (onClick) {
      onClick(user.id);
    }
  };

  // Get avatar size based on card size
  const getAvatarSize = () => {
    switch (size) {
      case 'small':
        return { width: 40, height: 40 };
      case 'large':
        return { width: 80, height: 80 };
      case 'medium':
      default:
        return { width: 60, height: 60 };
    }
  };

  // Get typography variants based on size
  const getTypographyVariants = () => {
    switch (size) {
      case 'small':
        return {
          displayName: 'body2' as const,
          username: 'caption' as const,
          stats: 'caption' as const,
        };
      case 'large':
        return {
          displayName: 'h6' as const,
          username: 'body2' as const,
          stats: 'body2' as const,
        };
      case 'medium':
      default:
        return {
          displayName: 'subtitle1' as const,
          username: 'body2' as const,
          stats: 'caption' as const,
        };
    }
  };

  const avatarSize = getAvatarSize();
  const typography = getTypographyVariants();
  
  // Use the follower/following counts from the user prop (already fetched from backend)
  const realCounts = {
    followerCount: user.followerCount || 0,
    followingCount: user.followingCount || 0,
    loading: false
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Card 
        variant={variant}
        sx={{
          p: 2,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': onClick ? {
            transform: 'translateY(-2px)',
          } : {},
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={avatarSize.width} height={avatarSize.height} />
            <Box flex={1}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
              {showStats && (
                <Box display="flex" gap={2} mt={1}>
                  <Skeleton variant="text" width={60} height={16} />
                  <Skeleton variant="text" width={60} height={16} />
                </Box>
              )}
            </Box>
            {showFollowButton && (
              <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      variant={variant}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
        } : {},
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" gap={2}>
          {/* User Avatar */}
          <Box position="relative">
            <Avatar
              src={user.profilePicture}
              sx={{
                ...avatarSize,
                border: '2px solid',
                borderColor: 'divider',
              }}
              component={onClick ? RouterLink : 'div'}
              to={onClick ? `/profile/${user.id}` : undefined}
            >
              {!user.profilePicture && <PersonIcon />}
            </Avatar>
            
            {/* Private account indicator */}
            {user.isPrivate && (
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  width: 20,
                  height: 20,
                }}
              >
                <LockIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>

          {/* User Info */}
          <Box flex={1} minWidth={0}>
            {/* Display Name and Verification */}
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant={typography.displayName}
                component="h3"
                fontWeight={600}
                noWrap
                sx={{
                  color: 'text.primary',
                  textDecoration: 'none',
                }}
              >
                {user.displayName}
              </Typography>
              
              {user.isVerified && (
                <VerifiedIcon 
                  sx={{ 
                    fontSize: size === 'small' ? 16 : 20, 
                    color: 'primary.main' 
                  }} 
                />
              )}
            </Box>

            {/* Username */}
            <Typography
              variant={typography.username}
              color="text.secondary"
              noWrap
              sx={{ mt: 0.25 }}
            >
              @{user.username}
            </Typography>

            {/* Stats */}
            {showStats && (
              <Box display="flex" gap={2} mt={1}>
                {realCounts.loading ? (
                  <>
                    <Skeleton variant="text" width={60} height={16} />
                    <Skeleton variant="text" width={60} height={16} />
                  </>
                ) : (
                  <>
                    <Typography variant={typography.stats} color="text.secondary">
                      <strong>{realCounts.followerCount.toLocaleString()}</strong> followers
                    </Typography>
                    <Typography variant={typography.stats} color="text.secondary">
                      <strong>{realCounts.followingCount.toLocaleString()}</strong> following
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {/* Private Account Badge */}
            {user.isPrivate && (
              <Chip
                label="Private"
                size="small"
                icon={<LockIcon />}
                sx={{
                  mt: 1,
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText',
                }}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Box className="action-buttons" display="flex" flexDirection="column" gap={1}>
            {showFollowButton && (
              <FollowButton
                targetUserId={user.id}
                size={size === 'large' ? 'medium' : 'small'}
                variant="outlined"
              />
            )}
            
            {showBlockButton && (
              <BlockButton
                targetUserId={user.id}
                targetUsername={user.username}
                isBlocked={false} // This should be passed from parent component
                onBlockChange={onBlockChange || (() => {})}
                size={size === 'large' ? 'medium' : 'small'}
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserProfileCard;